<template>
    <div class="quiz-summary-container">
        <div class="quiz-summary">
            <table>
                <tr>
                    <td>Full Marks</td>
                    <td class="px-5">:</td>
                    <th>{{ quiz.full_marks }}</th>
                </tr>
                <tr>
                    <td>Sections</td>
                    <td class="px-5">:</td>
                    <th>{{ sections.length }}</th>
                </tr>
                <tr>
                    <td>Questions</td>
                    <td class="px-5">:</td>
                    <th>{{ sections.map(section => section.section.questions.length).reduce((a, b) => a + b, 0) }}</th>
                </tr>
            </table>
        </div>
    </div>
    <div id="quiz-container" @click="makeCardEditable('quiz', 0)" class="quiz-card col-12 mt--15 shadow-sm"
    >
        <div v-show="currentlyEditingCardType==='quiz' && currentlyEditingCardIndex===0"
             class="editing-indicator"></div> <!-- Editing indicator -->
        <div class="card-content flex-column" style="padding: 24px">
            <FormElementWithoutLabel :error="errors.quiz.title"
            >
                <input :id="`title`"
                       type="text"
                       class="form-control"
                       placeholder="Quiz Title"
                       style="font: 24px Arial, Helvetica, sans-serif; border: none;border-bottom: 1px solid gray;"
                       ref="question_title"
                       v-bind:class="{ 'is-invalid' : errors.quiz.title }"
                       v-model="quiz.title"/>
            </FormElementWithoutLabel>

            <FormElementWithoutLabel :error="errors.quiz.description">
                <textarea :id="`description`"
                          placeholder="Quiz Description"
                          class="form-control"
                          v-bind:class="{ 'is-invalid' : errors.quiz.description }"
                          v-model="quiz.description">
                </textarea>
            </FormElementWithoutLabel>

            <div class="side-by-side-input-container">

                <FormElementWithoutLabel :error="errors.quiz.class_id"
                                         class="col-md-6 col-sm-12"
                >
                    <select :id="`quiz_class`"
                            class="form-control side-by-side-input-field"
                            @change="onClassChange($event)"
                            v-bind:class="{ 'is-invalid' : errors.quiz.class_id }"
                            v-model="quiz.class_id">
                        <option disabled value="">Select Class...</option>
                        <option v-for="clazz in classes" :selected="clazz[0] === quiz.class_id" :value="clazz[0]">
                            {{ clazz[1] }}
                        </option>
                    </select>
                </FormElementWithoutLabel>

                <FormElementWithoutLabel :error="errors.quiz.subject_id"
                                         class="col-md-6 col-sm-12">
                    <select :id="`quiz_subject`"
                            class="form-control side-by-side-input-field"
                            v-bind:class="{ 'is-invalid' : errors.quiz.subject_id }"
                            v-html="subjectOptionsList"
                            v-model="quiz.subject_id">
                    </select>
                </FormElementWithoutLabel>
            </div>

            <div class="side-by-side-input-container">
                <FormElementWithoutLabel :error="errors.quiz.full_marks"
                                         class="col-md-6 col-sm-12"
                >
                    <input :id="`full_marks`"
                           type="number"
                           placeholder="Full marks"
                           class="form-control side-by-side-input-field"
                           readonly
                           v-bind:class="{ 'is-invalid' : errors.quiz.full_marks }"
                           v-model="quiz.full_marks"/>
                </FormElementWithoutLabel>
                <FormElementWithoutLabel :error="errors.quiz.pass_marks"
                                         class="col-md-6 col-sm-12"
                >
                    <input :id="`pass_marks`"
                           type="number"
                           placeholder="Pass Marks"
                           class="form-control side-by-side-input-field"
                           v-bind:class="{ 'is-invalid' : errors.quiz.pass_marks }"
                           v-model="quiz.pass_marks"/>
                </FormElementWithoutLabel>
            </div>

        </div>
    </div>
    <div v-for="(section, index) in sections" :key="index">
        <Section
            :index="index"
            :section="section.section"
            :currently-editing-item-index="currentlyEditingItemIndex"
            :currently-editing-item-type="currentlyEditingItemType"
            :currently-editing-card-index="currentlyEditingCardIndex"
            :currently-editing-card-type="currentlyEditingCardType"
            :has-edit-permission="hasEditPermission"
            :error="section.error"
            :add-section="addSection"
            :duplicate-section="duplicateSection"
            :make-editable="makeEditable"
            :swap-section="swapSection"
            :make-card-editable="makeCardEditable"
            :total-section="sections.length"
            :remove-section="removeSection">

        </Section>
    </div>

    <button type="button" class="ladda-button btn btn-primary mb-20 w-120 mt-20"
            data-app-plugin="ladda" data-style="expand-left" id="mcq-submit-btn"
            @click="confirmSubmission">
        Save
    </button>
</template>
<script>
import {defineComponent, ref, watch} from 'vue';
import {VueDraggable} from "vue-draggable-plus";
import FormElement from "../../components/FormElement.vue";
import Question from "./components/Question.vue";
import FormElementWithoutLabel from "../../components/FormElementWithoutLabel.vue";
import Section from "./components/Section.vue";

export default defineComponent({
    components: {VueDraggable, Section, Question, FormElement, FormElementWithoutLabel},
    props: {
        classes: {
            type: Array,
            required: true,
        },
        subjects: {
            type: Array,
            required: true
        },
        formStoreUrl: {
            type: String,
            required: true,
        },
        formUpdateUrl: {
            type: String,
            required: true,
        },
        sections: {
            type: Array,
            required: false,
        },
        quiz: {
            type: Object,
            required: false,
        },
        isEdit: {
            type: Boolean,
            required: true,
        },
        hasEditPermission: {
            type: Boolean,
            required: true,
        },
        isDuplicating: {
            type: Boolean,
            required: true,
        }
    },
    setup(props) {
        const currentlyEditingItemType = ref('');
        const currentlyEditingItemIndex = ref(0);
        const currentlyEditingCardType = ref('quiz');
        const currentlyEditingCardIndex = ref(0);
        const subjectOptionsList = ref('<option disabled value="">Select Subject...</option>');
        const initializeErrorsBeforeShowingForm = () => {
            let errors = {
                quiz: {
                    ...props.quiz
                },
                sections: JSON.parse(JSON.stringify(props.sections)),
            }
            for (let attr in errors.quiz) {
                errors.quiz[attr] = '';
            }
            errors.sections.forEach(function (section) {
                for (let attr in section) {
                    if (attr === 'questions') {
                        section.questions.forEach(function (question) {
                                for (let attr in question) {
                                    if (attr === 'options') {
                                        question.options.forEach(
                                            function (option) {
                                                option.title = '';
                                                option.body = '';
                                                option.is_correct = '';
                                            }
                                        )
                                    } else {
                                        question[attr] = '';
                                    }
                                }
                            }
                        )
                    } else {
                        section[attr] = '';
                    }
                }
            })

            return errors;
        }

        const mergeErrorsWithSection = () => {
            let sectionsWithErrors = [];
            let errors = initializeErrorsBeforeShowingForm();

            for (let s = 0; s < props.sections.length; s++) {
                let questionList = [];
                let changedSection = props.sections[s];

                for (let q = 0; q < props.sections[s].questions.length; q++) {
                    let optionList = [];
                    let changedQuestion = props.sections[s].questions[q];

                    for (let o = 0; o < props.sections[s].questions[q].options.length; o++) {
                        let op = props.sections[s].questions[q].options[o];
                        let opEr = errors.sections[s].questions[q].options[o];
                        let opWithEr = {
                            option: op,
                            error: opEr
                        }
                        optionList.push(opWithEr);
                    }
                    changedQuestion.options = optionList;
                    let quesErr = {};
                    for (let attr in errors.sections[s].questions[q]) {
                        if (attr !== 'options') {
                            quesErr[attr] = errors.sections[s].questions[q].attr;
                        }
                    }
                    let quesWithEr = {
                        question: changedQuestion,
                        error: quesErr
                    }
                    questionList.push(quesWithEr);
                }
                changedSection.questions = questionList;
                let secErr = {};
                for (let attr in errors.sections[s]) {
                    if (attr !== 'questions') {
                        secErr[attr] = errors.sections[s].attr;
                    }
                }

                let secWithEr = {
                    section: changedSection,
                    error: secErr
                }
                sectionsWithErrors.push(secWithEr);
            }
            return sectionsWithErrors;
        }
        const sections = props.isEdit || props.isDuplicating ? ref(mergeErrorsWithSection()) : ref([{
            section: {
                title: '',
                body: '',
                settings: {
                    show_section_description_on_each_question: false,
                },
                questions: [{
                    question: {
                        title: '',
                        body: '',
                        answer_type: 'mcq',
                        is_answer_case_sensitive : false,
                        correct_points: 1.00,
                        incorrect_points: 0.00,
                        unanswered_points: 0.00,
                        options: [{
                            option: {
                                title: '',
                                body: '',
                                is_correct: false
                            },
                            error: {
                                title: '',
                                body: '',
                                is_correct: ''
                            }
                        }]
                    },
                    error: {
                        title: '',
                        body: '',
                        answer_type: '',
                        correct_points: '',
                        incorrect_points: '',
                        unanswered_points: '',
                    }
                }]
            },
            error: {
                title: '',
                body: '',
            }
        }])

        const quiz = props.isEdit || props.isDuplicating ? ref(props.quiz) : ref({
            title: '',
            description: '',
            subject_id: '',
            class_id: '',
            full_marks: '1.00',
            pass_marks: '',
        });

        watch(
            sections,
            () => {
                quiz.value.full_marks = sections.value.map(
                    section => section.section.questions.map(question => question.question.correct_points)
                        .reduce((a, b) => (a ? Number(a) : 0) + (b ? Number(b) : 0), 0)
                ).reduce((a, b) => (a ? Number(a) : 0) + (b ? Number(b) : 0), 0)
            },
            {deep: true}
        )

        return {
            subjectOptionsList,
            currentlyEditingItemType,
            currentlyEditingItemIndex,
            currentlyEditingCardType,
            currentlyEditingCardIndex,
            sections,
            errors: ref({
                quiz: {
                    title: '',
                    description: '',
                    subject_id: '',
                    class_id: '',
                    full_marks: '',
                    pass_marks: '',
                }
            }),
            quiz,
        }
    },
    mounted() {
        this.$refs.question_title.focus();
        this.populateSubjectOptions(this.quiz.class_id);
    },
    methods: {
        onClassChange(event) {
            const selectedClassId = event.target.value;
            this.populateSubjectOptions(selectedClassId, null);

        },
        makeEditable(type, index) {
            this.currentlyEditingItemType = type;
            this.currentlyEditingItemIndex = index;
        },
        makeCardEditable(type, index) {
            this.currentlyEditingCardType = type;
            this.currentlyEditingCardIndex = index;
        },
        populateSubjectOptions(selectClassId, subjectId) {
            let option = '';
            let tempString = '';
            let isSelected = false;
            this.subjects.forEach(function (subject) {
                if (subject[1][1] == selectClassId) {
                    isSelected = subjectId == subject[0];
                    option = `<option value=${subject[0]} ${isSelected ? 'selected' : ''} >${subject[1][0]}</option>`;
                    tempString += option;
                }
            });
            this.subjectOptionsList = tempString;
        },
        swapSection(prevIndex, nextIndex) {
            const temp = this.sections[prevIndex];
            this.sections[prevIndex] = this.sections[nextIndex];
            this.sections[nextIndex] = temp;
        },
        addSection(index) {
            this.sections.splice(index, 0,
                {
                    section: {
                        title: '',
                        body: 'Description (optional)',
                        settings: {
                            show_section_description_on_each_question: false,
                        },
                        questions: [{
                            question: {
                                title: '',
                                body: 'Question',
                                answer_type: 'mcq',
                                is_answer_case_sensitive: false,
                                correct_points: 1.00,
                                incorrect_points: 0.00,
                                unanswered_points: 0.00,
                                options: [{
                                    option: {
                                        title: '',
                                        body: 'Option',
                                        is_correct: false
                                    },
                                    error: {
                                        title: '',
                                        body: '',
                                        is_correct: ''
                                    }
                                }]
                            },
                            error: {
                                title: '',
                                body: '',
                                answer_type: '',
                                is_answer_case_sensitive: '',
                                correct_points: '',
                                incorrect_points: '',
                                unanswered_points: '',
                            }
                        }]
                    },
                    error: {
                        title: '',
                        body: '',
                    }
                }
            );
        },
        duplicateSection(index) {
            const clonedSection = JSON.parse(JSON.stringify(this.sections[index]))

            clonedSection.section.id = null
            clonedSection.section.questions = collect().unwrap(
                collect(clonedSection.section.questions).map((question) => {
                    question.question.id = null

                    question.question.options = collect().unwrap(
                        collect(question.question.options).map((option) => {
                            option.option.id = null

                            return option
                        })
                    )

                    return question
                })
            )

            this.sections.splice(index + 1, 0, clonedSection)
        },
        removeSection(index) {
            if (this.sections.length > 1) {
                this.sections.splice(index, 1);
            }
        },

        async confirmSubmission() {
            Swal.fire(
                {
                    title: 'Are you sure you want to save this quiz?',
                    icon: "warning",
                    showCancelButton: true,
                    confirmButtonColor: "#3B71CA",
                    confirmButtonText: "Save",
                    cancelButtonText: "Continue Editing",
                }
            ).then(result => {
                if (result.isConfirmed) {
                    this.submit()
                } else {
                    window.Ladda.stopAll()
                }
            });
        },

        async submit() {
            fetch(this.isEdit ? this.formUpdateUrl : this.formStoreUrl,
                {
                    method: 'post',
                    headers: new Headers({
                        'X-CSRF-TOKEN': $('meta[name="csrf-token"]').attr('content'),
                        'X-Requested-With': 'XMLHttpRequest',
                        'Content-Type': 'application/json'
                    }),
                    credentials: 'include',  // adds cookie header
                    body: JSON.stringify(this.submissionData())
                }
            ).then(async response => {
                if (response.status === 422) {
                    const json = await response.json()
                    this.transformSubmissionErrors(json.errors)
                    window.Ladda.stopAll()

                    const errors = new Set()
                    Object.values(json.errors).forEach(errorMessages => {
                        errorMessages.forEach(message => {
                            errors.add(message);
                        })
                    })

                    Swal.fire({
                        icon: 'error',
                        title: 'Failed saving quiz.',
                        html: `<p class="text-left text-danger">Please correct the following errors: <br/><br/>${Array.from(errors).join('<br/>')}</p>`,
                        footer: 'The above error messages can also be found below each corresponding input fields.'
                    })
                } else if (!response.ok) {
                    const error = await response.text()

                    Swal.fire({
                        icon: 'error',
                        title: 'Failed saving quiz.',
                        text: error,
                        footer: 'The above error messages can also be found below each corresponding input fields.'
                    })

                    window.Ladda.stopAll()
                } else if (response.redirected) {
                    toastr.success('Saved quiz successfully')

                    window.location.href = response.url
                }
            })
        },
        submissionData() {
            this.initializeErrorsBeforeSubmission();
            return {
                sections: this.trimErrorDataFromSections(),
                quiz: this.quiz,
            }
        },


        trimErrorDataFromSections() {
            let sectionWithoutErrors = JSON.parse(JSON.stringify(this.sections));
            sectionWithoutErrors.forEach(function (section) {
                delete section['error'];
                section.section.questions.forEach(function (question) {
                    delete question.error;
                    question.question.options.forEach(function (option) {
                        delete option.error;
                    });
                });
            });

            return sectionWithoutErrors.map(item => ({
                id: item.section.id ?? null,
                title: item.section.title,
                body: item.section.body,
                position: item.section.position,
                settings: item.section.settings,
                questions: item.section.questions.map(questionItem => ({
                    id: questionItem.question.id ?? null,
                    title: questionItem.question.title,
                    body: questionItem.question.body,
                    position: questionItem.question.position,
                    answer_type: questionItem.question.answer_type,
                    is_answer_case_sensitive: questionItem.question.is_answer_case_sensitive ?? false,
                    correct_points: questionItem.question.correct_points,
                    incorrect_points: questionItem.question.incorrect_points,
                    unanswered_points: questionItem.question.unanswered_points,
                    options: questionItem.question.options.map(optionItem => ({
                        id: optionItem.option.id ?? null,
                        title: optionItem.option.title,
                        position: optionItem.option.position,
                        body: optionItem.option.body,
                        is_correct: optionItem.option.is_correct,
                    })),
                }))
            }));
        },

        initializeErrorsBeforeSubmission() {
            for (let attr in this.errors.quiz) {
                this.errors.quiz[attr] = '';
            }
            this.sections.forEach(function (section) {
                for (let attr in section.error) {
                    section.error[attr] = '';
                }
                section.section.questions.forEach(function (question) {
                    for (let attr in question.error) {
                        question.error[attr] = '';
                    }
                    question.question.options.forEach(function (option) {
                        for (let attr in option.error) {
                            option.error[attr] = '';
                        }
                    });
                });
            });
        },

        transformSubmissionErrors(serverSideErrors) {
            for (let prop in serverSideErrors) {
                if (prop.startsWith('quiz')) {
                    const propParts = prop.split('.')
                    this.errors.quiz[propParts[1]] = serverSideErrors[prop][0]
                }
                if (prop.startsWith('sections')) {
                    const propParts = prop.split('.')
                    if (this.sections.length > parseInt(propParts[1])) {
                        if (propParts[2] === 'questions') {
                            if (propParts[4] === 'options') {
                                // option errors
                                //            sectionIndex            questionsAttr questionIndex               optionsAttr  optionsIndex
                                this.sections[propParts[1]]['section'][propParts[2]][propParts[3]]['question'][propParts[4]][propParts[5]]['error'][propParts[6]] = serverSideErrors[prop][0];
                            } else {
                                // should go to question error
                                this.sections[propParts[1]]['section'][propParts[2]][propParts[3]]['error'][propParts[4]] = serverSideErrors[prop][0]
                            }
                        } else {
                            // should go to section error
                            this.sections[propParts[1]]['section']['error'][propParts[2]] = serverSideErrors[prop][0];
                        }
                    }

                }
            }
        },
    }
})

</script>
<style>
.quiz-card {
    width: 100%;
    padding: 5px;
    border-radius: 5px;
    background-color: #ffffff;
    position: relative; /* Needed for the indicator */
}

/* Style for the editing indicator */
.editing-indicator {
    position: absolute;
    top: 0;
    left: 0;
    height: 100%;
    width: 5px;
    background-color: #32CD32; /* Green color */
    border-top-left-radius: 10px;
    border-bottom-left-radius: 10px;
}

.card-content {
    font-size: 14px;
    color: #333333;
}
</style>
<style scoped>

input[type="text"] {
    width: 100%;
    padding: 5px;
}

.quiz-title-input {
    font-size: 24px;
}

.side-by-side-input-container {
    display: flex;
    justify-content: space-between; /* Add space between the elements */
}

/* Style for the input fields */
.side-by-side-input-field {
    flex-grow: 1; /* Distribute available space equally */
}

.side-by-side-input-field:last-child {
    margin-right: 0;
}

@media screen and (max-width: 600px) {
    .side-by-side-input-container {
        flex-direction: column;
    }
}

.quiz-summary-container {
    align-items: center;
    display: flex;
    font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Oxygen-Sans, Ubuntu, Cantarell, "Helvetica Neue", sans-serif;
    text-align: left;
    background-color: #f8c5c5;
    color: maroon;
    position: fixed;
    top: 50%; /* Center vertically */
    right: 0; /* Fixed on the right side */
    transform: translateY(-40%); /* Adjust vertically */
}

@media (max-width: 576px) {
    .quiz-summary-container {
        top: auto;
        bottom: 0;
        left: 0;
        right: 0;
        justify-content: center;
        transform: none;
    }
}

.quiz-summary {
    padding: 10px 20px;
}

</style>
